import { useEffect, useState } from "react";
import { GraduationCap, Plus, Trash2, Loader2, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { DEMO_EXAMS, DEMO_EXAM_RESULTS } from "./mdtDemo";
import { inputCls, btnCls, toastOk } from "./RosterTab";

/* Exams — officers sit auto-graded MCQ boards; command authors them; supervisors see results. */

export default function ExamsTab({ G }) {
  const { serverId, token, demo, sfx, me } = G;
  const [exams, setExams] = useState(null);
  const [results, setResults] = useState([]);
  const [taking, setTaking] = useState(null);
  const [answers, setAnswers] = useState({});
  const [authoring, setAuthoring] = useState(false);
  const [draft, setDraft] = useState({ title: "", pass_mark: 70, questions: [{ q: "", options: ["", "", "", ""], correct: 0 }] });
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (demo) { setExams(DEMO_EXAMS); setResults(DEMO_EXAM_RESULTS); return; }
    api.get(`/nc/servers/${serverId}/cad/dept/exams`, { params: { token } }).then((r) => setExams(r.data.exams)).catch(() => setExams([]));
    if (me.is_supervisor) api.get(`/nc/servers/${serverId}/cad/dept/exams/results`, { params: { token } }).then((r) => setResults(r.data.results)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const submitTake = async () => {
    const exam = taking;
    const ans = exam.questions.map((q) => answers[q.id] ?? -1);
    if (ans.includes(-1)) return;
    sfx.keyTick();
    if (demo) {
      const correct = exam.questions.filter((q, i) => ans[i] === 0 || (q.id === "q2" && ans[i] === 1) || (q.id === "q3" && ans[i] === 1)).length;
      const score = Math.round(correct / exam.questions.length * 100);
      import("sonner").then(({ toast }) => (score >= exam.pass_mark ? toast.success : toast.error)(`${score >= exam.pass_mark ? "PASSED" : "FAILED"} — ${score}%`));
      setTaking(null); setAnswers({}); return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/cad/dept/exams/${exam.id}/take`, { answers: ans }, { params: { token } });
      import("sonner").then(({ toast }) => (r.data.result.passed ? toast.success : toast.error)(r.data.message));
      setTaking(null); setAnswers({}); load();
    } catch (e) { import("sonner").then(({ toast }) => toast.error(e.response?.data?.detail || "Failed")); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    const questions = draft.questions.filter((q) => q.q.trim() && q.options.filter((o) => o.trim()).length >= 2);
    if (!draft.title.trim() || !questions.length) return;
    sfx.keyTick();
    if (demo) { setExams((e) => [{ id: `l${Date.now()}`, title: draft.title, pass_mark: draft.pass_mark, questions: questions.map((q, i) => ({ id: `nq${i}`, q: q.q, options: q.options.filter((o) => o.trim()) })), created_by: me.name }, ...e]); setAuthoring(false); return; }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/cad/dept/exams`, { title: draft.title.trim(), pass_mark: draft.pass_mark, questions }, { params: { token } });
      toastOk(r.data.message); setAuthoring(false); setDraft({ title: "", pass_mark: 70, questions: [{ q: "", options: ["", "", "", ""], correct: 0 }] }); load();
    } catch (e) { import("sonner").then(({ toast }) => toast.error(e.response?.data?.detail || "Failed")); }
    finally { setBusy(false); }
  };

  if (!exams) return <div className="flex justify-center p-8"><Loader2 className="w-4 h-4 animate-spin text-sky-400" /></div>;

  /* taking view */
  if (taking) {
    return (
      <div className="p-3.5 flex flex-col gap-3" data-testid="exam-take">
        <p className="font-mono text-[11px] font-bold text-sky-300 uppercase tracking-[0.2em]">{taking.title} — pass at {taking.pass_mark}%</p>
        {taking.questions.map((q, qi) => (
          <div key={q.id} className="border border-white/10 bg-black/40 p-3">
            <p className="font-mono text-[10px] text-zinc-100 mb-2">Q{qi + 1}. {q.q}</p>
            <div className="flex flex-col gap-1">
              {q.options.map((o, oi) => (
                <button key={oi} data-testid={`exam-q${qi}-o${oi}`} onClick={() => setAnswers((a) => ({ ...a, [q.id]: oi }))}
                  className={`text-left font-mono text-[9.5px] px-2.5 py-1.5 border transition-colors ${answers[q.id] === oi ? "border-sky-400 text-sky-300 bg-sky-500/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>{o}</button>
              ))}
            </div>
          </div>
        ))}
        <div className="flex gap-2">
          <button data-testid="exam-submit" onClick={submitTake} disabled={busy || taking.questions.some((q) => answers[q.id] === undefined)}
            className={`${btnCls} border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10`}>{busy ? "Grading…" : "Submit for grading"}</button>
          <button data-testid="exam-abort" onClick={() => { setTaking(null); setAnswers({}); }} className={`${btnCls} border-white/15 text-zinc-400`}>Abort</button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3.5 flex flex-col gap-2" data-testid="exams-tab">
      {me.is_command && (
        <button data-testid="exam-author-open" onClick={() => { setAuthoring(!authoring); sfx.keyTick(); }}
          className={`${btnCls} border-sky-500/50 text-sky-300 hover:bg-sky-500/10 w-fit flex items-center gap-1`}><Plus className="w-3 h-3" /> Author exam</button>
      )}
      {authoring && (
        <div className="border border-sky-500/30 bg-sky-500/5 p-3 flex flex-col gap-2" data-testid="exam-author">
          <div className="flex gap-2">
            <input data-testid="exam-title" value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} placeholder="Exam title (e.g. Sergeant Board)" className={`${inputCls} flex-1`} />
            <label className="font-mono text-[8px] uppercase text-zinc-500 flex items-center gap-1">Pass %<input data-testid="exam-pass" type="number" min="1" max="100" value={draft.pass_mark} onChange={(e) => setDraft((d) => ({ ...d, pass_mark: +e.target.value }))} className={`${inputCls} w-16`} /></label>
          </div>
          {draft.questions.map((q, qi) => (
            <div key={qi} className="border border-white/10 p-2 flex flex-col gap-1.5">
              <input data-testid={`exam-draft-q${qi}`} value={q.q} onChange={(e) => setDraft((d) => ({ ...d, questions: d.questions.map((x, i) => i === qi ? { ...x, q: e.target.value } : x) }))} placeholder={`Question ${qi + 1}`} className={inputCls} />
              {q.options.map((o, oi) => (
                <div key={oi} className="flex items-center gap-1.5">
                  <input type="radio" name={`correct-${qi}`} data-testid={`exam-draft-q${qi}-c${oi}`} checked={q.correct === oi} onChange={() => setDraft((d) => ({ ...d, questions: d.questions.map((x, i) => i === qi ? { ...x, correct: oi } : x) }))} />
                  <input data-testid={`exam-draft-q${qi}-o${oi}`} value={o} onChange={(e) => setDraft((d) => ({ ...d, questions: d.questions.map((x, i) => i === qi ? { ...x, options: x.options.map((y, j) => j === oi ? e.target.value : y) } : x) }))} placeholder={`Option ${oi + 1}${q.correct === oi ? " (correct)" : ""}`} className={`${inputCls} flex-1`} />
                </div>
              ))}
            </div>
          ))}
          <div className="flex gap-2">
            <button data-testid="exam-add-q" onClick={() => setDraft((d) => ({ ...d, questions: [...d.questions, { q: "", options: ["", "", "", ""], correct: 0 }] }))} className={`${btnCls} border-white/15 text-zinc-300`}>+ Question</button>
            <button data-testid="exam-publish" onClick={publish} disabled={busy} className={`${btnCls} border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10`}>{busy ? "Publishing…" : "Publish exam"}</button>
          </div>
        </div>
      )}
      {exams.map((e) => (
        <div key={e.id} data-testid={`exam-${e.id.slice(0, 6)}`} className="border border-white/10 bg-black/40 p-3 flex items-center gap-3">
          <GraduationCap className="w-4 h-4 text-sky-400 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[11px] font-bold text-zinc-100">{e.title}</p>
            <p className="font-mono text-[8px] text-zinc-500 uppercase">{e.questions.length} questions · pass at {e.pass_mark}% · by {e.created_by || "command"}</p>
          </div>
          <button data-testid={`exam-start-${e.id.slice(0, 6)}`} onClick={() => { setTaking(e); sfx.keyTick(); }} className={`${btnCls} border-sky-500/50 text-sky-300 hover:bg-sky-500/10`}>Sit exam</button>
          {me.is_command && <button data-testid={`exam-del-${e.id.slice(0, 6)}`} onClick={() => { sfx.keyTick(); if (demo) setExams((x) => x.filter((y) => y.id !== e.id)); else api.delete(`/nc/servers/${serverId}/cad/dept/exams/${e.id}`, { params: { token } }).then(load); }} className={`${btnCls} border-red-500/40 text-red-300`}><Trash2 className="w-3 h-3" /></button>}
        </div>
      ))}
      {exams.length === 0 && <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.2em] p-3 text-center">No active exams — command authors them here</p>}
      {me.is_supervisor && results.length > 0 && (
        <div className="mt-2 border-t border-white/10 pt-2" data-testid="exam-results">
          <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">Recent sittings</p>
          {results.map((r) => (
            <div key={r.id} className="flex items-center gap-2 py-1 border-b border-white/5 font-mono text-[9px]">
              {r.passed ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <XCircle className="w-3 h-3 text-red-400" />}
              <span className="text-zinc-200">{r.name}</span><span className="text-zinc-500">{r.exam_title}</span>
              <span className={`ml-auto font-bold ${r.passed ? "text-emerald-300" : "text-red-300"}`}>{r.score}%</span>
              <span className="text-zinc-600">{String(r.at).slice(0, 10)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
